
import React, { useState, useEffect } from 'react';
import { X, Wallet, Calendar, Tag, AlertCircle, Clock, CheckCircle, CheckCircle2, DollarSign } from 'lucide-react';
import { formatCurrency } from '../utils/format';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import customerService from '../services/customerService';
import toast from 'react-hot-toast';
import { PaymentNoteModal } from './PaymentNoteModal';

interface CustomerBalanceDetailProps {
    customer: any;
    onClose: () => void;
    onPaymentSuccess: () => void;
}

// Utility to get expiration status (mirrors ApartadosPage logic)
const getExpirationStatus = (days: number) => {
    if (days >= 30) return { label: 'Vencido', color: 'text-red-700 bg-red-100', icon: <AlertCircle className="w-3 h-3" /> };
    if (days >= 21) return { label: 'Próximo a vence', color: 'text-orange-700 bg-orange-100', icon: <Clock className="w-3 h-3" /> };
    return { label: 'Vigente', color: 'text-green-700 bg-green-100', icon: <Calendar className="w-3 h-3" /> };
};

export const CustomerBalanceDetail: React.FC<CustomerBalanceDetailProps> = ({ 
    customer, 
    onClose, 
    onPaymentSuccess 
}) => {
    const [fullCustomer, setFullCustomer] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [selectedSale, setSelectedSale] = useState<any>(null);

    const loadCustomerDetails = async () => {
        try {
            setLoading(true);
            const data = await customerService.getCustomerById(customer.id);
            setFullCustomer(data);
        } catch (error) {
            console.error('Error loading customer details:', error);
            toast.error('Error al cargar detalles del cliente');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCustomerDetails();
    }, [customer.id]);

    const pendingSales = fullCustomer?.sales?.filter((s: any) => {
        const balance = s.total - (s.amountPaid || 0);
        // Una nota es pendiente si tiene saldo O si tiene pre-orders que aún no han sido recibidos
        const hasPendingPreOrders = s.sale_items?.some((i: any) => i.isPreOrder && (!i.product?.invoice_id || i.product?.in_transit));
        return (s.status !== 'COMPLETADA' || hasPendingPreOrders) && (balance > 0.01 || hasPendingPreOrders);
    }) || [];

    const handleOpenPayment = (sale: any) => {
        setSelectedSale(sale);
        setIsPaymentModalOpen(true);
    };

    // Logic for manual delivery removed in favor of automatic invoice-based status
    const isPaymentModalOpen = !!selectedSale;
    const setIsPaymentModalOpen = (open: boolean) => !open && setSelectedSale(null);

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] animate-fade-in p-4" onClick={onClose}>
            <div 
                className="bg-white w-full max-w-5xl max-h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-zoom-in" 
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-purple-600 text-white">
                    <div>
                        <h2 className="text-2xl font-black">{customer.fullName}</h2>
                        <div className="flex flex-col gap-1 mt-1">
                          <p className="text-purple-100 text-sm flex items-center gap-2">
                              <Wallet className="w-4 h-4" />
                              Saldo Total: <span className="font-bold underline">{formatCurrency(customer.totalBalance)}</span>
                          </p>
                          {customer.phone && (
                            <p className="text-purple-200 text-xs font-medium">{customer.phone}</p>
                          )}
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-full transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                    <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-4 px-1">Notas con Saldo Pendiente</h3>
                    
                    {loading ? (
                        <div className="space-y-4">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-xl"></div>
                            ))}
                        </div>
                    ) : pendingSales.length === 0 ? (
                        <div className="bg-white p-12 rounded-3xl border border-dashed border-gray-200 text-center shadow-inner">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                              <CheckCircle className="w-8 h-8 text-gray-300" />
                            </div>
                            <p className="text-gray-500 font-bold">No hay notas pendientes de liquidación</p>
                            <p className="text-gray-400 text-xs mt-1">Esta clienta está al día con sus pagos.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {pendingSales.map((sale: any) => {
                                const balance = sale.total - (sale.amountPaid || 0);
                                const seniority = getExpirationStatus(sale.dias_transcurridos || 0);
                                const productCount = sale.sale_items?.length || sale.items?.length || 0;

                                return (
                                    <div key={sale.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all overflow-hidden group">
                                        <div className="p-6 flex flex-col xl:flex-row xl:items-start justify-between gap-8">
                                            <div className="flex-1">
                                                <div className="flex flex-wrap items-center gap-3 mb-4">
                                                    <span className="text-xl font-black text-purple-700">{sale.folio || `#${sale.id}`}</span>
                                                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase ${
                                                        sale.status === 'PRE-ORDER' ? 'bg-amber-100 text-amber-600' : 'bg-purple-100 text-purple-600'
                                                    }`}>
                                                        {sale.status}
                                                    </span>
                                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black uppercase ${seniority.color}`}>
                                                        {seniority.icon}
                                                        {seniority.label} ({sale.dias_transcurridos || 0}d)
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap gap-6 mb-6">
                                                    <div className="flex items-center gap-2.5 text-sm text-gray-500 font-medium">
                                                        <div className="p-1.5 bg-gray-100 rounded-lg text-gray-400">
                                                            <Calendar className="w-4 h-4" />
                                                        </div>
                                                        {format(new Date(sale.createdAt), 'dd MMMM yyyy', { locale: es })}
                                                    </div>
                                                    <div className="flex items-center gap-2.5 text-sm text-gray-500 font-medium">
                                                        <div className="p-1.5 bg-gray-100 rounded-lg text-gray-400">
                                                            <Tag className="w-4 h-4" />
                                                        </div>
                                                        {productCount} {productCount === 1 ? 'producto' : 'productos'}
                                                    </div>
                                                </div>

                                                {/* Detalle de Productos */}
                                                <div className="mt-4 bg-gray-50/50 rounded-xl p-3 border border-gray-100/50">
                                                    <div className="space-y-2">
                                                        {(sale.sale_items || []).map((item: any) => (
                                                            <div key={item.id} className="flex justify-between items-start gap-4 text-[11px] leading-tight pb-2 border-b border-gray-200/50 last:border-0 last:pb-0">
                                                                <div className="flex-1">
                                                                    <p className="font-black text-gray-800 uppercase line-clamp-1">{item.productName}</p>
                                                                    <div className="flex flex-wrap gap-2 mt-1">
                                                                        <span className="text-gray-400 font-mono">Cod: {item.productCode || 'S/C'}</span>
                                                                        {item.talla && (
                                                                            <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-bold uppercase">
                                                                                Talla: {item.talla}
                                                                            </span>
                                                                        )}
                                                                        {item.color && (
                                                                            <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-bold uppercase">
                                                                                Color: {item.color}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    
                                                                    {/* Estado de entrega por producto */}
                                                                    <div className="mt-2 flex items-center gap-1.5">
                                                                        {(!item.isPreOrder) || (item.product?.invoice_id && !item.product?.in_transit) ? (
                                                                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-green-600 bg-green-50 px-2 py-0.5 rounded">
                                                                                <CheckCircle2 className="w-3 h-3" /> RECIBIDO / EN STOCK
                                                                            </span>
                                                                        ) : (
                                                                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                                                                                <Clock className="w-3 h-3" /> PENDIENTE (PRE-ORDER)
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="text-right whitespace-nowrap">
                                                                    <p className="text-gray-500 font-bold">x{item.quantity}</p>
                                                                    <p className="text-purple-600 font-black">{formatCurrency(item.unitPrice)}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-4 w-full xl:w-72 shrink-0">
                                                <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-100 text-center xl:text-right">
                                                    <p className="text-[10px] text-red-500 font-black uppercase tracking-widest leading-none mb-3">Saldo a Cobrar</p>
                                                    <p className="text-4xl font-black text-red-600 tabular-nums leading-none">
                                                        {formatCurrency(balance)}
                                                    </p>
                                                    <div className="flex items-center justify-center xl:justify-end gap-2 mt-4 pt-4 border-t border-red-100/50">
                                                       <p className="text-[11px] text-gray-500 font-bold uppercase tracking-tight">Total de Nota:</p>
                                                       <p className="text-sm font-black text-gray-700">{formatCurrency(sale.total)}</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex flex-col gap-3">
                                                    {balance > 0.01 && (
                                                        <button
                                                            onClick={() => handleOpenPayment(sale)}
                                                            className="w-full bg-purple-600 text-white px-6 py-4 rounded-2xl hover:bg-purple-700 transition-all shadow-xl shadow-purple-100 flex items-center justify-center gap-3 text-xs font-black uppercase tracking-widest hover:-translate-y-1 active:translate-y-0"
                                                        >
                                                            <DollarSign className="w-6 h-6" />
                                                            <span>Liquidar Saldo</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Pago */}
            {isPaymentModalOpen && selectedSale && (
                <PaymentNoteModal
                    sale={selectedSale}
                    onClose={() => setSelectedSale(null)}
                    onSuccess={() => {
                        setSelectedSale(null);
                        loadCustomerDetails();
                        onPaymentSuccess();
                    }}
                />
            )}
        </div>
    );
};
